import * as React from "react";
import { useMappedState } from "redux-react-hook";
import _ from "lodash";
import styled from "styled-components";

import { useEventListener } from "src/library/hooks/useEventListener";
import {
  ActionsPopOut,
  IStep
} from "src/library/components/ActionsPopOut/index";
import {
  Button,
  ButtonType
} from "src/library/components/Button";
import { Input } from "src/library/components/Forms";
import { Icons } from "src/library/components/Icon/types";
import tokenizeString from "src/library/utils/string-manipulations/tokenize-string";
import { IStore } from "src/core/shared/store";
import { IDrop } from "src/core/drops/api/drops.api";
import SelectOrderVariant from "src/core/orders/components/SelectOrderVariant";
import { IOrderVariant } from "src/core/orders/api/orders.api";
import { useCurrentUser } from "src/core/users/hooks";
import { UserOrderableDropSelect } from "src/core/drops/components/UserOrderableDropMultiSelect";

//
// TODO: Update interface once we know what it is
// TODO: show modal on confirm
// TODO: if multiple messages on date click - show all

interface IListContextProps {
  createList: (name: string, orderVariant?: IOrderVariant | null, dropOverrideId?: number) => void;
  loading: boolean;
  drop: IDrop | null;
  $buttonType?: ButtonType;
}

export const CreateListContext: React.ReactFCWithChildren<IListContextProps> = ({
  createList,
  loading,
  drop,
  $buttonType = "primary"
}) => {
  const {
    terminology, orderVariants, installedModules
  } = useMappedState(({ global }: IStore) => ({
    terminology: global.settings?.terminology,
    orderVariants: global.settings?.orderVariants || [],
    installedModules: global.settings?.installedModules || []

  }));

  const [ position, setPosition ] = React.useState({
    x: 0,
    y: 0
  });

  const [ newListName, setNewListName ] = React.useState<string>("");
  const divRef = React.useRef<HTMLDivElement>(null);
  const [ showAction, setShowAction ] = React.useState(false);
  const [ selectedOrderVariantId, setSelectedOrderVariantId ] = React.useState<number | null>(null);
  const [ { currentRole } ] = useCurrentUser();
  const isCustomerAdmin = React.useMemo(() => currentRole?.code === "customer-admin", [ currentRole?.code ]);
  const [ manuallySelectedDrop, setManuallySelectedDrop ] = React.useState<IDrop | null>(null);

  const selectedDrop = React.useMemo(() => {
    return manuallySelectedDrop || (currentRole?.code !== "customer-admin" ? drop : null);
  }, [
    currentRole?.code,
    drop,
    manuallySelectedDrop
  ]);

  const selectedOrderVariant = React.useMemo(() => {
    return _.find(orderVariants, ov => ov.id === selectedOrderVariantId);
  }, [ orderVariants, selectedOrderVariantId ]);

  const toggleShowAction = () => {
    // Open up action dropdown for user:
    setShowAction(!showAction);
  };

  React.useEffect(() => {
    if (showAction) {
      setOverlayPosition(true);
    }
  }, [ showAction ]);

  const setOverlayPosition = (autoScroll = false) => {
    if (divRef.current) {
      const bounds = divRef.current.getBoundingClientRect();
      let right = bounds.right - bounds.width;

      if (window.outerWidth <= 500) {
        right = 0;
      } else if (right + 400 > window.outerWidth) {
        right = bounds.right - 300;
      }

      setPosition({
        y: bounds.top + bounds.height,
        x: right
      });

      if (autoScroll) {
        const scrollWrapper = document.getElementById("main-wrapper");

        if (scrollWrapper) {
          const currentBottom = bounds.top + 350;
          const currentScroll = scrollWrapper.scrollTop;

          if (currentBottom > window.outerHeight) {
            scrollWrapper.scrollTo({
              top: currentScroll + (currentBottom - window.outerHeight),
              behavior: "smooth"
            });
          }
        }
      }
    }
  };

  // Step to select what type of order variant the order will be
  const orderVariantSelectionStep = React.useMemo(() => {
    if ((selectedDrop?.variantsAvailable && selectedDrop?.variantsAvailable?.length > 1) || (installedModules.includes("zertus"))) {
      const variantSelectionStep: IStep = {
        title: "Select type of list",
        allowNext: true,
        hideFooter: false,
        component: (
          <SelectOrderVariant
            orderVariant={selectedOrderVariant}
            handleUpdate={setSelectedOrderVariantId}
          />
        )
      };

      return variantSelectionStep;
    }

    return null;
  }, [
    installedModules,
    selectedDrop?.variantsAvailable,
    selectedOrderVariant
  ]);

  const dropSelectionStep = React.useMemo(() => {
    if (isCustomerAdmin) {
      const dropSelectionStep: IStep = {
        title: `Select ${terminology?.drop.singular.text}`,
        allowNext: !!selectedDrop,
        hideFooter: false,
        component: (
          <SelectDropWrapper>
            <UserOrderableDropSelect
              onDropsChange={drop => setManuallySelectedDrop(drop)}
              selectedDrop={selectedDrop}
              orderVariantId={selectedOrderVariant?.id ?? null}
            />
          </SelectDropWrapper>
        )
      };

      return dropSelectionStep;
    }

    return null;
  }, [
    isCustomerAdmin,
    selectedDrop,
    selectedOrderVariant?.id,
    terminology?.drop.singular.text
  ]);

  const createListSteps = React.useMemo(() => {
    const steps: IStep[] = [];

    // zertus need to select order variant first
    if (orderVariantSelectionStep && installedModules.includes("zertus")) {
      steps.push(orderVariantSelectionStep);
    }

    //  standard customer drop selection
    if (dropSelectionStep) {
      steps.push(dropSelectionStep);
    }

    //  order variant selection if this is a zertus instance, we have already picked it
    if (orderVariantSelectionStep && !installedModules.includes("zertus")) {
      steps.push(orderVariantSelectionStep);
    }

    steps.push(
      {
        title: "Choose a name for your list",
        component: <Input
          name="listName"
          label="List name"
          as="input"
          handleChange={e => setNewListName(e.target.value)}
                   />,
        padComponent: true,
        allowNext: newListName.length > 0
      });

    return steps;
  }, [
    dropSelectionStep,
    installedModules,
    newListName.length,
    orderVariantSelectionStep
  ]);

  const handler = React.useCallback(() => {
    setOverlayPosition();
  }, []);

  useEventListener("resize", handler);
  useEventListener("scroll", handler, document.getElementById("main-wrapper"));

  return (
    <>
      <div style={{ position: "relative" }}>
        <Button
          icon={Icons.TABLE}
          $buttonType={$buttonType}
          onClick={toggleShowAction}
          size="medium"
          loading={loading}
        >
          {`Create a new ${terminology ? tokenizeString("%LIST%", terminology) : "list"}`}
        </Button>

        {showAction && (
          <ActionsPopOut
            {...position}
            action={() => createList(newListName, selectedOrderVariant, selectedDrop?.id)}
            handleClose={() => {
              toggleShowAction();
              setNewListName("");
              setManuallySelectedDrop(null);
              setSelectedOrderVariantId(null);
            }}
            directionHorizontal="left"
            directionVertical="top"
            steps={createListSteps}
          />
        )}
      </div>
    </>
  );
};

const SelectDropWrapper = styled.div`
  display: flex;
  padding: ${({ theme }) => theme.spacing.padding.md};
  flex-direction: column;
  padding-top: 0;
  > * {
    margin-top: ${({ theme }) => theme.spacing.padding.sm};
  }
`;
